import { multi, withDependencies, named } from '@wix/thunderbolt-ioc'
import type { IocContainer, ProviderCreator } from '@wix/thunderbolt-ioc'
import type { IRendererPropsExtender, ServiceProvider, Experiments, ILogger } from '@wix/thunderbolt-symbols'
import {
	SiteServicesManagerSymbol,
	ServiceProviderSymbol,
	ServiceProviderAdapterSymbol,
	SignalsServiceProviderSymbol,
	ExperimentsSymbol,
	SiteFeatureConfigSymbol,
	LoggerSymbol,
} from '@wix/thunderbolt-symbols'
import { globalServicesManagerController } from '@wix/services-manager'
import { ServicesManagerProvider } from '@wix/services-manager-react'
import type { ServicesManager } from '@wix/services-manager/types'
import { createServicesManagerFromProviders, addServicesIfMissing } from '@wix/viewer-services-manager'
import { name, SiteServicesManagerProviderSymbol } from './symbols'
import type { ServiceRegistrarSiteConfig } from './types'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

const gsmKey = Math.random().toString(36)

type SiteServicesManagerResolver = {
	get: () => ServicesManager
	getAsync: () => Promise<ServicesManager>
}

const reportServicesManagerResolutionError = (logger: ILogger, error: unknown) => {
	logger.captureError(error instanceof Error ? error : new Error(String(error)), {
		tags: {
			feature: name,
			errorType: 'siteServicesManagerResolution',
		},
	})
}

export const SiteServiceManager = withDependencies<ServicesManager>(
	[multi(ServiceProviderSymbol), SignalsServiceProviderSymbol, ExperimentsSymbol, named(SiteFeatureConfigSymbol, name)],
	(
		servicesProviders: Array<ServiceProvider>,
		signalsServiceProvider: ServiceProvider,
		experiments: Experiments,
		siteConfig: ServiceRegistrarSiteConfig
	): ServicesManager => {
		const isBuilder = siteConfig.isBuilderComponentModel
		const isServicesInfraOpen = isExperimentOpen(experiments, 'specs.thunderbolt.servicesInfra') || isBuilder
		const services = [signalsServiceProvider]
		const manager = createServicesManagerFromProviders(isServicesInfraOpen ? [...servicesProviders, ...services] : [])
		if (isExperimentOpen(experiments, 'specs.thunderbolt.globalServiceManager')) {
			globalServicesManagerController.setGlobalServicesManager(manager, gsmKey)
		}
		return manager
	}
)

/**
 * Lazily resolves the site `ServicesManager`. Bound via `toProvider`, so resolving
 * `SiteServicesManagerProviderSymbol` yields this getter *without* building the manager.
 *
 * This matters because building the manager eagerly initializes every registered service
 * (`createServicesManager` instantiates all bindings up front), and a service's factory may
 * synchronously check out another service (e.g. ConsentPolicy requires the Url service). On
 * the client, `getRenderer()` (which constructs the renderer-props extenders) runs concurrently
 * with `loadSiteFeatures()` — the phase that registers these service providers. Resolving the
 * manager during that window can snapshot an incomplete provider set and throw
 * "Service ... is not provided". Deferring resolution to the getter call (inside
 * `extendRendererProps`, which runs after `loadSiteFeatures` completes) guarantees every
 * selected provider is registered before any service is instantiated.
 */
export const SiteServicesManagerProvider: ProviderCreator<SiteServicesManagerResolver> =
	(container: IocContainer) => () => ({
		get: () => container.get<ServicesManager>(SiteServicesManagerSymbol),
		getAsync: () => container.getAsync<ServicesManager>(SiteServicesManagerSymbol),
	})

export const ServiceRegistrarPropsExtender = withDependencies(
	[SiteServicesManagerProviderSymbol, LoggerSymbol],
	(getSiteServicesManager: () => SiteServicesManagerResolver, logger: ILogger): IRendererPropsExtender => {
		const resolveSiteServicesManager = (resolve: () => Promise<ServicesManager> | ServicesManager) => {
			try {
				return Promise.resolve(resolve()).catch((error) => {
					reportServicesManagerResolutionError(logger, error)
					throw error
				})
			} catch (error) {
				reportServicesManagerResolutionError(logger, error)
				throw error
			}
		}

		const siteServicesManagerResolver = getSiteServicesManager()

		return {
			async extendRendererProps() {
				const servicesManager = await resolveSiteServicesManager(siteServicesManagerResolver.getAsync)
				return { servicesManager, ServicesManagerProvider }
			},
		}
	}
)

/**
 * Adapter for services that depend on features which themselves depend on services.
 * Use ServiceProviderAdapterSymbol instead of ServiceProviderSymbol to register services
 * that would otherwise cause circular dependencies in the IoC container.
 *
 * This adapter collects service providers registered via ServiceProviderAdapterSymbol
 * and adds them to the ServicesManager after its initial creation, breaking the dependency cycle.
 */
export const SiteServicesManagerAdapter = withDependencies<ServicesManager>(
	[
		multi(ServiceProviderAdapterSymbol),
		SiteServicesManagerSymbol,
		ExperimentsSymbol,
		named(SiteFeatureConfigSymbol, name),
	],
	(
		serviceAdapters: Array<ServiceProvider>,
		servicesManager: ServicesManager,
		experiments: Experiments,
		siteConfig: ServiceRegistrarSiteConfig
	): ServicesManager => {
		const isBuilder = siteConfig.isBuilderComponentModel
		const isServicesInfraOpen = isExperimentOpen(experiments, 'specs.thunderbolt.servicesInfra') || isBuilder
		addServicesIfMissing(servicesManager, isServicesInfraOpen ? serviceAdapters : [])
		return servicesManager
	}
)
